import { CheckRounded } from '@mui/icons-material';
import { Divider, ListItem, ListItemText, Menu, Skeleton, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ReactComponent as SignOutIcon } from 'assets/icon-sign-out.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/icon-cog.svg';
import { useGlobalAppBackdropStore } from 'components/full-page-blocker/GlobalAppBackdrop';
import { getRoutePath } from 'config/routes';
import { logout } from 'features/customer/store/actions';
import {
  getIsApiCustomer,
  getUserEmail,
  getUserImage,
  getUserName,
} from 'features/customer/store/selectors';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { ProfileOrWorkspaceSidebarItem } from 'features/pageSidebar/sidebarItems/actions/ProfileOrWorkspaceSidebarItem';
import { SidebarItem } from 'features/pageSidebar/sidebarItems/SidebarItem';
import { SidebarNavItem } from 'features/pageSidebar/sidebarItems/SidebarNavItem';
import { useSidebarStore } from 'features/pageSidebar/useSidebarStore';
import {
  invalidateGetAllCustomerWorkspaceIdsQuery,
  useGetAllCustomerWorkspacesInfo,
} from 'features/workspaces/useGetAllCustomerWorkspacesInfo';
import { setWorkspaceId } from 'features/workspaces/workspaceSlice';
import { PlusIcon } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { MouseEvent, useRef } from 'react';
import { httpCreateWorkspace } from 'services/backofficeIntegration/http/endpoints/workspace/httpCreateWorkspace';
import gtmIds from 'services/tracking/GTMIds';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import styled from 'styled-components';
import useDropdown from 'utils/hooks/useDropdown';
import useTr from 'utils/hooks/useTr';
import { delayExecution, withTestId } from 'utils/utils';

export const SidebarItemAccountWithWorkspace = () => {
  const dispatch = useAppDispatch();
  const translate = useTr();

  const canHaveMultipleWorkspaces = useFeatureFlagEnabled('multiple-workspaces');

  const { anchorElement, isOpen, setAnchorElement, close } = useDropdown();

  const isApiCustomer = useAppSelector(getIsApiCustomer);
  const userName = useAppSelector(getUserName);
  const userEmail = useAppSelector(getUserEmail);
  const userImage = useAppSelector(getUserImage);
  const activeWorkspaceId = useAppSelector(state => state.workspace.workspaceId);

  const triggerElementRef = useRef<HTMLDivElement>(null);

  const { dataFiltered: workspaces, isLoading: isLoadingWorkspacesInfo } =
    useGetAllCustomerWorkspacesInfo();

  const { mutateAsync: createWorkspace, isPending: isCreatingWorkspace } = useMutation({
    mutationFn: () => httpCreateWorkspace.callEndpoint(),
    onSuccess: () => {
      invalidateGetAllCustomerWorkspaceIdsQuery();
    },
  });

  const showGlobalBackdrop = useGlobalAppBackdropStore(state => state.show);
  const setSettingsViewOpen = useSidebarStore(state => state.setSettingsViewOpen);
  const isSidebarExpanded = useSidebarStore(state => state.isExpanded);

  const isWorkflowsInLoadingState = isLoadingWorkspacesInfo || isCreatingWorkspace;

  const handleMenuClose = () => {
    close();
  };

  const handleMenuTriggerClick = async (event: MouseEvent<HTMLElement>) => {
    // API customer doesn't have workspaces, therefore opening the settings redirect directly
    if (isApiCustomer) {
      setSettingsViewOpen(true);
      handleMenuClose();
      return;
    }

    // A click on the sidebar will expand it.
    // If we open the menu instantly, it will be positioned incorrectly.
    // Therefore we add a small delay before opening the menu.
    await delayExecution(isSidebarExpanded ? 0 : 310);

    // Note: We can't use the open() shortcut from useDropdown here,
    //       because it uses currentTarget from the event,
    //       which is not longer available after the delay.
    setAnchorElement(triggerElementRef.current);
  };

  const handleCreateNewWorkspaceClick = async () => {
    await createWorkspace();
  };

  const handleSwitchWorkspace = (event: MouseEvent<HTMLElement>, workspaceId: string) => {
    event.stopPropagation();
    event.preventDefault();

    dispatch(setWorkspaceId(workspaceId));

    // Reload page to ensure all new data is fetched
    window.location.reload();
  };

  return (
    <>
      <ProfileOrWorkspaceSidebarItem
        ref={triggerElementRef}
        gtmId={gtmIds.sidebar.accountMenu}
        onClick={handleMenuTriggerClick}
        tooltip="layout.sidebar.actions.profile.menu_trigger"
        testAttribute="sidebar-account-menu-trigger"
        headline={userName}
        image={userImage}
        subline={userEmail}
      />

      <StyledMenu
        {...withTestId('account-menu-dropdown')}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={close}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <CaptionListItem>
          <ListItemText>
            <Typography variant="caption" color="text.secondary" textTransform="uppercase">
              <FormattedMessage id="layout.sidebar.account_menu.headline_profile" />
            </Typography>
          </ListItemText>
        </CaptionListItem>

        <ProfileOrWorkspaceSidebarItem
          to={getRoutePath('customerSettingsGeneral')}
          headline={userName}
          image={userImage}
          subline={userEmail}
        />

        <CaptionListItem>
          <ListItemText>
            <Typography variant="caption" color="text.secondary" textTransform="uppercase">
              <FormattedMessage id="layout.sidebar.account_menu.headline_workspaces" />
            </Typography>
          </ListItemText>
        </CaptionListItem>

        {isWorkflowsInLoadingState || workspaces.length === 0 ? (
          <ListItem>
            <ListItemText>
              <Skeleton variant="rounded" width="100%" height={56} />
            </ListItemText>
          </ListItem>
        ) : (
          [
            ...workspaces.map(workspaceInfo => {
              const isActiveWorkspace = activeWorkspaceId === workspaceInfo.id;

              return (
                <StyledWorkspaceSidebarItem
                  key={workspaceInfo.id}
                  headline={workspaceInfo.name}
                  image={userImage}
                  subline={translate('layout.sidebar.account_menu.workspace_subline', {
                    count: workspaceInfo.memberCount,
                  })}
                  endAdornment={isActiveWorkspace && <CheckRounded />}
                  onClick={
                    isActiveWorkspace
                      ? undefined
                      : event => handleSwitchWorkspace(event, workspaceInfo.id)
                  }
                />
              );
            }),

            ...(canHaveMultipleWorkspaces
              ? [
                  <StyledSidebarItemWithSpacingTop
                    key="createNewWorkspace"
                    gtmId={gtmIds.sidebar.createNewWorkspace}
                    icon={<PlusIcon />}
                    name="layout.sidebar.actions.workspace.create_new"
                    onClick={handleCreateNewWorkspaceClick}
                    testAttribute="account-menu-create-workspace-item"
                  />,
                ]
              : []),
          ]
        )}

        <StyledDivider />

        <SidebarNavItem
          gtmId={gtmIds.sidebar.accountMenuItem}
          icon={<SettingsIcon />}
          to={getRoutePath('customerSettingsGeneral')}
          name="layout.sidebar.actions.account.profile"
          testAttribute="sidebar-settings-button"
          onClick={() => {
            setSettingsViewOpen(true);
            handleMenuClose();
          }}
        />

        <SidebarItem
          gtmId={gtmIds.sidebar.accountMenuItem}
          icon={<SignOutIcon />}
          name="layout.sidebar.actions.profile.sign_out"
          onClick={() => {
            showGlobalBackdrop();

            dispatch(logout.request());
            handleMenuClose();
          }}
          testAttribute="account-menu-signout-item"
        />
      </StyledMenu>
    </>
  );
};

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    border-radius: ${({ theme }) => theme.borderRadius.one};
    padding: ${({ theme }) => theme.spacings.two};
  }

  .MuiList-root {
    width: 330px;
  }
`;

const CaptionListItem = styled(ListItem)`
  &.MuiListItem-root {
    padding-block: 0;
  }
`;

const StyledDivider = styled(Divider)`
  margin-bottom: ${({ theme }) => theme.spacings.three};
`;

const StyledWorkspaceSidebarItem = styled(ProfileOrWorkspaceSidebarItem)`
  & .MuiButtonBase-root {
    margin-bottom: 0;
  }
`;

const StyledSidebarItemWithSpacingTop = styled(SidebarItem)`
  margin-top: ${({ theme }) => theme.spacings.one};
`;
